// HerbalSoapTestimonials.js - Herbal soap themed testimonial slider section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed Testimonial section with slider functionality for testimonial cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The testimonial section elements
 */
export const createHerbalSoapTestimonialsElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const testimonialSectionClass = 'herbal-soap-testimonial-section';
  const testimonialContainerClass = 'herbal-soap-testimonial-container';
  const testimonialHeaderClass = 'herbal-soap-testimonial-header';
  const testimonialTitleClass = 'herbal-soap-testimonial-title';
  const testimonialSubtitleClass = 'herbal-soap-testimonial-subtitle';
  const testimonialSliderClass = 'herbal-soap-testimonial-slider';
  const testimonialSlideClass = 'herbal-soap-testimonial-slide';
  const testimonialCardClass = 'herbal-soap-testimonial-card';
  const testimonialControlsClass = 'herbal-soap-testimonial-controls';
  
  // Desktop Styles - Herbal theme with natural green background
  const testimonialSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f0fdf4',
    background: `
      radial-gradient(circle at 30% 20%, rgba(144, 238, 144, 0.12) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(34, 139, 34, 0.08) 0%, transparent 50%),
      linear-gradient(135deg, #f0fdf4 0%, #dcfce7 25%, #bbf7d0 50%, #dcfce7 75%, #f0fdf4 100%)
    `,
    color: '#1a3d1a',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const testimonialContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '40px'
  };

  const testimonialHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const testimonialTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const testimonialSubtitleStyles = {
    fontSize: '20px',
    color: '#4a5568',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const testimonialSliderStyles = {
    position: 'relative',
    width: '100%',
    height: '400px',
    overflow: 'hidden',
    backgroundColor: 'transparent'
  };

  const testimonialSlideStyles = {
    flex: '0 0 100%',
    height: '100%',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '10px'
  };

  const testimonialCardStyles = {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    border: '1px solid rgba(144, 238, 144, 0.25)',
    boxShadow: '0 10px 30px rgba(34, 139, 34, 0.12)',
    padding: '30px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#1a3d1a',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden'
  };

  const testimonialControlsStyles = {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '25px'
  };

  // Tablet Styles
  const tabletTestimonialTitleStyles = {
    fontSize: '40px'
  };

  const tabletTestimonialSubtitleStyles = {
    fontSize: '18px'
  };

  // Mobile Styles
  const mobileTestimonialSectionStyles = {
    ...testimonialSectionStyles,
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const mobileTestimonialTitleStyles = {
    fontSize: '32px'
  };

  const mobileTestimonialSubtitleStyles = {
    fontSize: '16px'
  };

  const mobileTestimonialSliderStyles = {
    ...testimonialSliderStyles,
    height: '350px'
  };

  const mobileTestimonialCardStyles = {
    ...testimonialCardStyles,
    padding: '25px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(testimonialSectionClass, testimonialSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialContainerClass, testimonialContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialHeaderClass, testimonialHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialTitleClass, testimonialTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSubtitleClass, testimonialSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSliderClass, testimonialSliderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSlideClass, testimonialSlideStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialCardClass, testimonialCardStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialControlsClass, testimonialControlsStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(testimonialTitleClass, tabletTestimonialTitleStyles);
    newRegistry[DEVICES.TABLET].set(testimonialSubtitleClass, tabletTestimonialSubtitleStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(testimonialSectionClass, mobileTestimonialSectionStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialTitleClass, mobileTestimonialTitleStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialSubtitleClass, mobileTestimonialSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialSliderClass, mobileTestimonialSliderStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialCardClass, mobileTestimonialCardStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    testimonialSectionClass,
    testimonialContainerClass,
    testimonialHeaderClass,
    testimonialTitleClass,
    testimonialSubtitleClass,
    testimonialSliderClass,
    testimonialSlideClass,
    testimonialCardClass,
    testimonialControlsClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  
  // Header elements
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  
  // Slider elements
  const sliderId = sectionId + 5;
  const maskId = sliderId + 1;
  const leftArrowId = sliderId + 2;
  const rightArrowId = sliderId + 3;
  
  // Controls elements
  const controlsId = sliderId + 4;
  const autoplayToggleId = sliderId + 5;
  const loopToggleId = sliderId + 6;
  
  // Slide IDs
  const slide1Id = sliderId + 100;
  const slide2Id = sliderId + 200;
  const slide3Id = sliderId + 300;
  const slide4Id = sliderId + 400;

  // Card IDs
  const card1Id = slide1Id + 1000;
  const card2Id = slide2Id + 1000;
  const card3Id = slide3Id + 1000;
  const card4Id = slide4Id + 1000;

  // Array of herbal soap testimonial data
  const testimonials = [
    {
      name: 'Sarah Johnson',
      position: 'Skincare Enthusiast',
      company: 'Natural Beauty Blogger',
      quote: 'I\'ve been using HerbalSoap products for 6 months and my skin has never felt better! The turmeric soap cleared my acne and the lavender soap is so relaxing. 100% natural and chemical-free!',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 5
    },
    {
      name: 'Michael Chen',
      position: 'Organic Store Owner',
      company: 'Green Earth Organics',
      quote: 'We\'ve been selling HerbalSoap products in our store for over a year. Customers love the quality and natural ingredients. The neem soap is our bestseller - it\'s amazing for sensitive skin!',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      position: 'Mother of Two',
      company: 'Family Wellness Advocate',
      quote: 'As a mom, I\'m very careful about what I use on my family\'s skin. HerbalSoap\'s honey and aloe vera soap is gentle enough for my kids yet effective for adults. We\'re customers for life!',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 5
    },
    {
      name: 'David Thompson',
      position: 'Dermatologist',
      company: 'Natural Skin Clinic',
      quote: 'I recommend HerbalSoap to my patients with sensitive skin conditions. The coconut oil and essential oil blend provides excellent moisturizing without harsh chemicals. Professional quality!',
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 4
    }
  ];

  // Create a simple slide with a card child
  const createSlide = (slideId, cardId, index) => {
    return {
      id: slideId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Slide ${index}`,
      parentId: maskId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialSlideClass,
      sliderRole: 'slide',
      styles: {
        flex: '0 0 33.3333%',
        height: '100%'
      },
      text: '',
      children: [cardId],
      visibility: 'visible',
      isContainer: true,
      attributes: {
        'data-element': 'testimonial-slide',
        'data-slide-index': index - 1
      }
    };
  };

  // Create simple slides
  const slide1 = createSlide(slide1Id, card1Id, 1);
  const slide2 = createSlide(slide2Id, card2Id, 2);
  const slide3 = createSlide(slide3Id, card3Id, 3);
  const slide4 = createSlide(slide4Id, card4Id, 4);
  
  // Create direct references to each testimonial
  const sarahData = testimonials[0];
  const michaelData = testimonials[1];
  const emilyData = testimonials[2];
  const davidData = testimonials[3];
  
  // Create custom card creation function for fixed testimonials
  const createFixedCard = (id, parentId, testimonialData) => {
    const uniqueBase = id * 100;
    
    // Create IDs for child elements
    const ratingId = uniqueBase + 10;
    const quoteId = uniqueBase + 20;
    const authorContainerId = uniqueBase + 30;
    const avatarId = uniqueBase + 31;
    const authorInfoId = uniqueBase + 32;
    const nameId = uniqueBase + 33;
    const positionId = uniqueBase + 34;
    const companyId = uniqueBase + 35;
    
    // Create the main card element
    const cardElement = {
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Card with ${testimonialData.name}`,
      parentId: parentId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialCardClass,
      styles: {},
      text: '',
      children: [ratingId, quoteId, authorContainerId],
      visibility: 'visible',
      isContainer: true,
      attributes: {
        'data-element': 'testimonial-card',
        'data-testimonial-name': testimonialData.name
      }
    };
    
    // Create all child elements
    const childElements = [];
    
    // 1. Rating stars
    childElements.push({
      id: ratingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Rating Stars',
      parentId: id,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        gap: '3px',
        marginBottom: '15px',
        color: '#90EE90',
        fontSize: '16px'
      },
      text: '★'.repeat(testimonialData.rating),
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-rating',
        'data-rating': testimonialData.rating
      }
    });
    
    // 2. Quote text
    childElements.push({
      id: quoteId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Quote',
      parentId: id,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '16px',
        lineHeight: '1.8',
        color: '#4a5568',
        marginBottom: '25px',
        fontStyle: 'italic',
        position: 'relative',
        paddingLeft: '10px',
        borderLeft: '3px solid #90EE90',
        width: '100%'
      },
      text: testimonialData.quote,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-quote'
      }
    });
    
    // 3. Author container
    childElements.push({
      id: authorContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Author Container',
      parentId: id,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginTop: 'auto',
        width: '100%'
      },
      text: '',
      children: [avatarId, authorInfoId],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-container'
      }
    });
    
    // 4. Avatar
    childElements.push({
      id: avatarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Author Avatar',
      parentId: authorContainerId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '3px solid #90EE90',
        boxShadow: '0 3px 10px rgba(144, 238, 144, 0.3)'
      },
      src: testimonialData.avatar,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-avatar',
        'data-avatar-for': testimonialData.name,
        'alt': testimonialData.name
      }
    });
    
    // 5. Author info container
    childElements.push({
      id: authorInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Author Info',
      parentId: authorContainerId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column'
      },
      text: '',
      children: [nameId, positionId, companyId],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-info'
      }
    });
    
    // 6. Author name
    childElements.push({
      id: nameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Author Name',
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('h4'),
      styles: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#1a3d1a',
        marginBottom: '4px',
        marginTop: '0'
      },
      text: testimonialData.name,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-name'
      }
    });
    
    // 7. Author position
    childElements.push({
      id: positionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Author Position',
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#90EE90',
        marginBottom: '2px',
        marginTop: '0'
      },
      text: testimonialData.position,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-position'
      }
    });
    
    // 8. Author company
    childElements.push({
      id: companyId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Author Company',
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#4a5568',
        marginTop: '0'
      },
      text: testimonialData.company,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-company'
      }
    });
    
    return {
      card: cardElement,
      children: childElements
    };
  };
  
  // Create cards for each slide
  const card1Result = createFixedCard(card1Id, slide1Id, sarahData);
  const card2Result = createFixedCard(card2Id, slide2Id, michaelData);
  const card3Result = createFixedCard(card3Id, slide3Id, emilyData);
  const card4Result = createFixedCard(card4Id, slide4Id, davidData);

  // Create slider arrow as link with image
  const createArrow = (id, direction) => {
    const imageId = id + 1000;
    
    return {
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      sliderRole: 'arrow',
      direction: direction,
      text: '',
      styles: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        backgroundColor: 'rgba(144, 238, 144, 0.9)',
        border: 'none',
        borderRadius: '50%',
        boxShadow: '0 4px 15px rgba(144, 238, 144, 0.3)',
        cursor: 'pointer',
        zIndex: 5,
        textDecoration: 'none',
        left: direction === 'left' ? '10px' : 'auto',
        right: direction === 'right' ? '10px' : 'auto'
      },
      children: [imageId],
      parentId: sliderId,
      visibility: 'visible',
      hierarchyName: getHierarchyName('link'),
      interactions: {
        events: [
          {
            id: Date.now() + (direction === 'left' ? 1 : 2),
            type: 'click',
            action: 'custom',
            label: `${direction === 'left' ? 'Previous' : 'Next'} slide`,
            value: {
              script: `
                const slider = event.target.closest('[data-element-type="slider"]');
                if (!slider) return;
                
                const mask = slider.querySelector('[data-slider-role="mask"]');
                if (!mask) return;
                
                let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 1);
                let slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || 1);
                const loopEnabled = slider.getAttribute('data-loop') === 'true';
                
                const windowWidth = window.innerWidth;
                let currentDevice = 'desktop';
                
                if (windowWidth <= 768) {
                  currentDevice = 'mobile';
                } else if (windowWidth <= 1024) {
                  currentDevice = 'tablet';
                }
                
                const slides = mask.children.length;
                let currentSlide = parseInt(mask.getAttribute('data-current-slide') || 0);
                
                if ('${direction}' === 'left') {
                  if (currentSlide === 0 && loopEnabled) {
                    currentSlide = slides - slidesPerView;
                  } else {
                    currentSlide = Math.max(0, currentSlide - slidesPerScroll);
                  }
                } else {
                  if (currentSlide >= slides - slidesPerView && loopEnabled) {
                    currentSlide = 0;
                  } else {
                    currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
                  }
                }
                
                mask.style.transition = 'transform 0.5s ease-in-out';
                mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                mask.setAttribute('data-current-slide', currentSlide);
                
                event.preventDefault();
              `
            }
          }
        ]
      },
      attributes: {
        'href': '#',
        'aria-label': direction === 'left' ? 'Previous slide' : 'Next slide',
        'data-direction': direction
      }
    };
  };

  // Create arrow image element
  const createArrowImage = (id, parentId, direction) => ({
    id,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'image',
    text: '',
    styles: {
      width: '20px',
      height: '20px',
      objectFit: 'contain',
    },
    src: direction === 'left' 
      ? 'https://cdn-icons-png.flaticon.com/512/130/130882.png'
      : 'https://cdn-icons-png.flaticon.com/512/130/130884.png',
    children: [],
    parentId: parentId,
    visibility: 'visible',
    hierarchyName: getHierarchyName('image')
  });
  
  // Create left and right arrows
  const leftArrow = createArrow(leftArrowId, 'left');
  const rightArrow = createArrow(rightArrowId, 'right');

  // Create arrow image elements
  const leftArrowImage = createArrowImage(leftArrowId + 1000, leftArrowId, 'left');
  const rightArrowImage = createArrowImage(rightArrowId + 1000, rightArrowId, 'right');

  // Create structural elements
  const structuralElements = [
    // Main Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Testimonial Section',
      parentId: null,
      hierarchyName: 'Herbal Soap Testimonial Section',
      className: testimonialSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'testimonial-section',
        'data-component': 'section',
        'data-section-type': 'testimonial',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonial Container',
      parentId: sectionId,
      hierarchyName: 'Testimonial Container',
      className: testimonialContainerClass,
      styles: {},
      children: [headerId, sliderId, controlsId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'testimonial-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonial Header',
      parentId: containerId,
      hierarchyName: 'Testimonial Header',
      className: testimonialHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'testimonial-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Testimonial Title',
      parentId: headerId,
      hierarchyName: 'Testimonial Title',
      className: testimonialTitleClass,
      styles: {},
      children: [],
      text: 'What Our Customers Say',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'testimonial-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Testimonial Subtitle',
      parentId: headerId,
      hierarchyName: 'Testimonial Subtitle',
      className: testimonialSubtitleClass,
      styles: {},
      children: [],
      text: 'Discover why thousands of customers love our natural herbal soaps and how they\'ve transformed their skincare routine with pure, organic ingredients.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'testimonial-subtitle'
      }
    },
    
    // Slider Container
    {
      id: sliderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'slider',
      name: 'Testimonial Slider',
      parentId: containerId,
      hierarchyName: 'Testimonial Slider',
      className: testimonialSliderClass,
      sliderRole: 'container',
      styles: {},
      children: [maskId, leftArrowId, rightArrowId],
      text: '',
      slidesPerView: 3,
      slidesPerScroll: 1,
      deviceSettings: {
        desktop: {
          slidesPerView: 3,
          slidesPerScroll: 1
        },
        tablet: {
          slidesPerView: 2,
          slidesPerScroll: 1
        },
        mobile: {
          slidesPerView: 1,
          slidesPerScroll: 1
        }
      },
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'testimonial-slider',
        'data-element-type': 'slider',
        'data-loop': 'false',
        'data-autoplay': 'false',
        'data-delay': '5000'
      }
    },
    
    // Slider Mask
    {
      id: maskId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Slider Mask',
      parentId: sliderId,
      hierarchyName: 'Slider Mask',
      sliderRole: 'mask',
      styles: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        transition: 'transform 0.5s ease-in-out',
        cursor: 'grab'
      },
      children: [slide1Id, slide2Id, slide3Id, slide4Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'slider-mask',
        'data-current-slide': '0',
        'data-slides-count': '4'
      }
    },
    
    // Left slider arrow
    leftArrow,
    
    // Right slider arrow
    rightArrow,
    
    // Controls container
    {
      id: controlsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Slider Controls',
      parentId: containerId,
      hierarchyName: 'Slider Controls',
      className: testimonialControlsClass,
      styles: {},
      children: [autoplayToggleId, loopToggleId],
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'testimonial-controls'
      }
    },
    
    // Autoplay toggle
    {
      id: autoplayToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Autoplay Toggle',
      parentId: controlsId,
      hierarchyName: 'Autoplay Toggle',
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'autoplay-toggle'
      },
      innerHTML: `
        <label for="autoplay-toggle-${sectionId}" style="font-size: 14px; color: #4a5568;">Autoplay</label>
        <input type="checkbox" id="autoplay-toggle-${sectionId}" style="cursor: pointer;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-autoplay', this.checked ? 'true' : 'false');
          }
        ">
      `
    },
    
    // Loop toggle
    {
      id: loopToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Loop Toggle',
      parentId: controlsId,
      hierarchyName: 'Loop Toggle',
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'loop-toggle'
      },
      innerHTML: `
        <label for="loop-toggle-${sectionId}" style="font-size: 14px; color: #4a5568;">Loop Slides</label>
        <input type="checkbox" id="loop-toggle-${sectionId}" style="cursor: pointer;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-loop', this.checked ? 'true' : 'false');
          }
        ">
      `
    }
  ];
  
  // Build elements array
  const newElements = []; 
  
  // Add structural elements
  structuralElements.forEach(element => {
    if (element.sliderRole !== 'slide') {
      newElements.push(element);
    }
  });
  
  // Add slides
  newElements.push(slide1);
  newElements.push(slide2);
  newElements.push(slide3);
  newElements.push(slide4);
  
  // Add cards
  newElements.push(card1Result.card);
  newElements.push(card2Result.card);
  newElements.push(card3Result.card);
  newElements.push(card4Result.card);
  
  // Add arrow images
  newElements.push(leftArrowImage);
  newElements.push(rightArrowImage);
  
  // Add card child elements
  card1Result.children.forEach(element => newElements.push(element));
  card2Result.children.forEach(element => newElements.push(element));
  card3Result.children.forEach(element => newElements.push(element));
  card4Result.children.forEach(element => newElements.push(element));
  
  return {
    elements: newElements
  };
}; 